import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/finally';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import { SweetAlertHelper } from '../../../core/helpers/sweet-alert-helper.service';
import {
  IComplaintDashboardFilterOptions,
  IComplaintDashboardSummary,
  IComplaintDashboardTrendItem,
  IComplaintDashboardTrendRequest,
  IComplaintsDashboardFilters,
  IComplaintsDashboardView,
} from '../../../core/interfaces/complaints-dashboard.interface';
import { ComplaintsService } from '../../../services/complaints.service';
import { ParentModule } from '../../A2-utils/ParentClass/parent-module';
import {
  buildDashboard,
  buildSummaryParams,
  buildTrend,
  buildTrendParams,
  DEFAULT_DASHBOARD_OPTIONS,
  resolveDateRange,
} from './complaints-dashboard.helper';
import { ComplaintsDashboardStore } from './complaints-dashboard.store';

@Component({
  selector: 'app-complaints-dashboard',
  templateUrl: './complaints-dashboard.component.html',
  styleUrls: ['./complaints-dashboard.component.scss'],
  providers: [ComplaintsDashboardStore],
})
export class ComplaintsDashboardComponent
  extends ParentModule
  implements OnInit, OnDestroy
{
  private today = new Date();
  private trendItems: IComplaintDashboardTrendItem[];
  private filters$ = new Subject<IComplaintsDashboardFilters>();
  private subscriptions: Subscription[] = [];
  private allowedView = false;

  constructor(
    private complaintsService: ComplaintsService,
    private sweetAlertHelper: SweetAlertHelper,
    private store: ComplaintsDashboardStore,
    protected inject: Injector,
  ) {
    super('complaint', inject);
  }

  ngOnInit() {
    this.watchViewPermission();
  }

  private watchViewPermission() {
    this.subscriptions.push(
      this.actionsList$.subscribe({
        next: () => {
          if (!this.allowedView && this.isAllowedAction('View')) {
            this.allowedView = true;
            this.loadDashboard();
          }
        },
      }),
    );
  }

  private loadDashboard() {
    this.subscriptions.push(
      this.complaintsService.getDashboardFilters().subscribe({
        next: (options: IComplaintDashboardFilterOptions) =>
          this.store.setFilterOptions(options),
        error: (err) => this.sweetAlertHelper.captureException(err),
      }),
      this.filters$
        .switchMap((filters) => this.loadSummary(filters))
        .subscribe({
          next: (view) => this.store.setView(view),
        }),
      this.filters$
        .map((filters) => JSON.stringify(buildTrendParams(filters)))
        .distinctUntilChanged(
          (prev, next) => prev === next && !this.store.trendFailed,
        )
        .switchMap((params) => this.loadTrend(JSON.parse(params)))
        .subscribe({
          next: (items) => {
            this.trendItems = items;
            this.store.setTrendLoading(false);
            this.renderTrend();
          },
        }),
      this.store.filters$.subscribe({
        next: (filters) => this.refresh(filters),
      }),
    );
  }

  private refresh(filters: IComplaintsDashboardFilters) {
    this.today = new Date();
    this.filters$.next(filters);
    this.renderTrend();
  }

  private renderTrend() {
    this.store.setTrend(
      this.trendItems &&
        buildTrend(
          this.trendItems,
          resolveDateRange(this.store.filters, this.today),
        ),
    );
  }

  private loadTrend(
    params: IComplaintDashboardTrendRequest,
  ): Observable<IComplaintDashboardTrendItem[]> {
    this.store.setTrendLoading(true);
    this.store.setTrendFailed(false);
    return this.complaintsService.getDashboardTrend(params).catch(() => {
      this.trendItems = undefined;
      this.store.setTrend(undefined);
      this.store.setTrendLoading(false);
      this.store.setTrendFailed(true);
      return Observable.empty<IComplaintDashboardTrendItem[]>();
    });
  }

  private loadSummary(
    filters: IComplaintsDashboardFilters,
  ): Observable<IComplaintsDashboardView> {
    this.store.setLoadFailed(false);
    this._loading.show();
    return this.complaintsService
      .getDashboardSummary(
        buildSummaryParams(filters, DEFAULT_DASHBOARD_OPTIONS, this.today),
      )
      .map((summary: IComplaintDashboardSummary) =>
        buildDashboard(summary, filters, DEFAULT_DASHBOARD_OPTIONS),
      )
      .catch((err) => {
        this.store.setView(undefined);
        this.store.setLoadFailed(true);
        this.sweetAlertHelper.captureException(err);
        return Observable.empty<IComplaintsDashboardView>();
      })
      .finally(() => this._loading.hiden());
  }

  get allowView(): boolean {
    return this.allowedView;
  }

  get accessDenied(): boolean {
    return !this.onApplyingRules && !this.allowedView;
  }

  get hasView(): boolean {
    return !!this.store.view;
  }

  get loadFailed(): boolean {
    return this.store.loadFailed;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/complaints/complaints-dashboard/complaints-dashboard.component.ts